import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import styles from './InfoTooltip.module.css';

interface InfoTooltipProps {
  text: string;
  className?: string;
}

/** Ícono (i) con burbuja explicativa al pasar el mouse o enfocar con teclado.
 *  Úsese junto a etiquetas de métricas para explicar qué son, cómo se
 *  calculan y para qué sirven.
 *
 *  La burbuja se renderiza en un PORTAL con position:fixed — muchas tarjetas
 *  del dashboard tienen `overflow: hidden` (por el acento/filo superior), y
 *  una burbuja absoluta dentro del wrapper quedaba recortada por el borde del
 *  panel. Se posiciona sobre el ícono, cae hacia abajo si no hay espacio
 *  arriba, y se sujeta a los bordes del viewport.
 */
export function InfoTooltip({ text, className }: InfoTooltipProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; below: boolean } | null>(null);

  const show = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    // Sin espacio arriba → la burbuja cae debajo del ícono.
    const below = r.top < 110;
    // Clamp horizontal: mitad del max-width de la burbuja (260px) + margen.
    // Solo aplica con un viewport real (>300px) — en contextos embebidos u
    // ocultos innerWidth puede ser 0 y el clamp mandaría la burbuja fuera.
    const half = 138;
    const centro = r.left + r.width / 2;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const left = vw > 300 ? Math.min(Math.max(centro, half), vw - half) : centro;
    setPos({ top: below ? r.bottom + 8 : r.top - 8, left, below });
  };
  const hide = () => setPos(null);

  return (
    <span
      className={`${styles.wrapper} ${className ?? ''}`}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label="Qué significa esta métrica"
        onFocus={show}
        onBlur={hide}
      >
        <Info size={12} aria-hidden="true" />
      </button>
      {pos && createPortal(
        <span
          role="tooltip"
          className={`${styles.bubble} ${pos.below ? styles.bubbleBelow : ''}`}
          style={{ top: pos.top, left: pos.left }}
        >
          {text}
        </span>,
        document.body,
      )}
    </span>
  );
}
